import { z } from 'zod';

import { ZodHashedIdResponse } from '@Common/types/zod/zod-hash-id';

export const ORDER_PREFIX = 'order';

export enum TRANSACTION_ACTION_ID_PREFIX {
  MaterializedOrder = 'materialized_order',
  Alpaca = 'alpaca',
  CardTopUp = 'cardtopup',
  CardAuthorization = 'card_authorization',
  ApplePayTopUp = 'apple_pay_topup',
  ApplePayAuthorization = 'apple_pay_authorization',
  Coinbase = 'coinbase',
  OmnibusDividend = 'omnibus_dividend',
  Upvest = 'upvest',
  InvestmentPlan = 'investment_plan',
  AutoInvest = 'auto_invest',
  SkippedAllocation = 'skipped_allocation',
  Oddo = 'oddo',
  FullyManagedPlanContribution = 'fully_managed_plan_contribution',
  FullyManagedPlanMatchingContribution = 'fully_managed_plan_matching_contribution',
  FullyManagedPortfolioRebalancing = 'fully_managed_portfolio_rebalancing',
  EmployeeSavingsCustodyFee = 'employee_savings_custody_fee',
  EmployeeSavingsManagementFee = 'employee_savings_management_fee',
  EmployeeSavingsOutgoingTransfer = 'employee_savings_outgoing_transfer',
  EmployeeSavingsPayout = 'employee_savings_payout',
  EmployeeSavingsWithdrawal = 'employee_savings_withdrawal',
  EmployeeSavingsEarlyWithdrawal = 'employee_savings_early_withdrawal',
  EmployeeSavingsCampaignContribution = 'employee_savings_campaign_contribution',
  TriggeredOrder = 'triggered_order',
  TriggeredArbitrageOrder = 'b2b_triggered_arbitrage_order',
  PlannedWithdrawalOrder = 'b2b_planned_withdrawal_order',
  ManagedInvestmentPlanManagementFee = 'managed_investment_plan_management_fee',
  VoluntaryPayment = 'voluntary_payment',
  MatchingContribution = 'matching_contribution',
  Arbitrage = 'arbitrage',
  IncomingExternalTransfer = 'b2b_incoming_external_transfer',
  Taiga = 'taiga',
  EltifOrder = 'eltif_order',
  StructuredProductOrder = 'structured_product_order',
  MatchingContributionOrder = 'b2b_matching_contribution_order',
  ErCampaignAllocation = 'er_campaign_allocation',
  MergerCA = 'merger_ca',
  SpinoffCA = 'spinoff_ca',
  DividendCA = 'dividend_ca',
  CapitalIncrease = 'capital_increase',
  CorrectedVoluntaryPayment = 'corrected_voluntary_payment',
  CorrectedArbitrage = 'corrected_arbitrage',
  CorrectedOddo = 'corrected_oddo',
  CorrectedMatchingContribution = 'corrected_matching_contribution',
  CorrectedMatchingContributionOrder = 'corrected_b2b_matching_contribution_order',
  CorrectedEmployeeSavingsCampaignContribution = 'corrected_employee_savings_campaign_contribution',
  CorrectedFullyManagedPlanContribution = 'corrected_fully_managed_plan_contribution',
  CorrectedFullyManagedPlanMatchingContribution = 'corrected_fully_managed_plan_matching_contribution',
  CryptoTransfer = 'crypto_transfer',
}

export const B2C_TRANSACTION_ACTION_ID_PREFIX = [
  TRANSACTION_ACTION_ID_PREFIX.MaterializedOrder,
  TRANSACTION_ACTION_ID_PREFIX.Alpaca,
  TRANSACTION_ACTION_ID_PREFIX.CardTopUp,
  TRANSACTION_ACTION_ID_PREFIX.CardAuthorization,
  TRANSACTION_ACTION_ID_PREFIX.ApplePayTopUp,
  TRANSACTION_ACTION_ID_PREFIX.ApplePayAuthorization,
  TRANSACTION_ACTION_ID_PREFIX.Coinbase,
  TRANSACTION_ACTION_ID_PREFIX.OmnibusDividend,
  TRANSACTION_ACTION_ID_PREFIX.Upvest,
  TRANSACTION_ACTION_ID_PREFIX.InvestmentPlan,
  TRANSACTION_ACTION_ID_PREFIX.AutoInvest,
  TRANSACTION_ACTION_ID_PREFIX.SkippedAllocation,
  TRANSACTION_ACTION_ID_PREFIX.TriggeredOrder,
  TRANSACTION_ACTION_ID_PREFIX.Oddo,
  TRANSACTION_ACTION_ID_PREFIX.ManagedInvestmentPlanManagementFee,
  TRANSACTION_ACTION_ID_PREFIX.CryptoTransfer,
] as const;

export const B2B_TRANSACTION_ACTION_ID_PREFIX = [
  TRANSACTION_ACTION_ID_PREFIX.Oddo,
  TRANSACTION_ACTION_ID_PREFIX.FullyManagedPlanContribution,
  TRANSACTION_ACTION_ID_PREFIX.FullyManagedPlanMatchingContribution,
  TRANSACTION_ACTION_ID_PREFIX.FullyManagedPortfolioRebalancing,
  TRANSACTION_ACTION_ID_PREFIX.EmployeeSavingsCustodyFee,
  TRANSACTION_ACTION_ID_PREFIX.EmployeeSavingsManagementFee,
  TRANSACTION_ACTION_ID_PREFIX.EmployeeSavingsOutgoingTransfer,
  TRANSACTION_ACTION_ID_PREFIX.EmployeeSavingsPayout,
  TRANSACTION_ACTION_ID_PREFIX.EmployeeSavingsWithdrawal,
  TRANSACTION_ACTION_ID_PREFIX.EmployeeSavingsEarlyWithdrawal,
  TRANSACTION_ACTION_ID_PREFIX.EmployeeSavingsCampaignContribution,
  TRANSACTION_ACTION_ID_PREFIX.TriggeredArbitrageOrder,
  TRANSACTION_ACTION_ID_PREFIX.VoluntaryPayment,
  TRANSACTION_ACTION_ID_PREFIX.CorrectedVoluntaryPayment,
  TRANSACTION_ACTION_ID_PREFIX.MatchingContribution,
  TRANSACTION_ACTION_ID_PREFIX.Arbitrage,
  TRANSACTION_ACTION_ID_PREFIX.CorrectedArbitrage,
  TRANSACTION_ACTION_ID_PREFIX.IncomingExternalTransfer,
  TRANSACTION_ACTION_ID_PREFIX.Taiga,
  TRANSACTION_ACTION_ID_PREFIX.MatchingContributionOrder,
  TRANSACTION_ACTION_ID_PREFIX.ErCampaignAllocation,
  TRANSACTION_ACTION_ID_PREFIX.MergerCA,
  TRANSACTION_ACTION_ID_PREFIX.SpinoffCA,
  TRANSACTION_ACTION_ID_PREFIX.DividendCA,
  TRANSACTION_ACTION_ID_PREFIX.CapitalIncrease,
  TRANSACTION_ACTION_ID_PREFIX.PlannedWithdrawalOrder,
  TRANSACTION_ACTION_ID_PREFIX.CorrectedOddo,
  TRANSACTION_ACTION_ID_PREFIX.CorrectedMatchingContribution,
  TRANSACTION_ACTION_ID_PREFIX.CorrectedMatchingContributionOrder,
  TRANSACTION_ACTION_ID_PREFIX.CorrectedEmployeeSavingsCampaignContribution,
  TRANSACTION_ACTION_ID_PREFIX.CorrectedFullyManagedPlanContribution,
  TRANSACTION_ACTION_ID_PREFIX.CorrectedFullyManagedPlanMatchingContribution,
] as const;

export const ZodTransactionOrderMove = z.enum(['buy', 'sell']);
export type TransactionOrderMove = z.infer<typeof ZodTransactionOrderMove>;

export const ZodTransactionOrderStatus = z.enum([
  'pending',
  'filled',
  'cancelled',
  'expired',
  'rejected',
  'skipped',
]);
export type TransactionOrderStatus = z.infer<typeof ZodTransactionOrderStatus>;

export const ZodCryptoOrderStatus = z.enum(['pending', 'cancelled', 'filled', 'partially_filled']);
export type CryptoOrderStatus = z.infer<typeof ZodCryptoOrderStatus>;

export const ZodTransactionReferralOrigin = z.object({
  user_id: ZodHashedIdResponse,
  role: z.enum(['referee', 'referrer']),
});
export type TransactionReferralOrigin = z.infer<typeof ZodTransactionReferralOrigin>;

export const ZodTransactionReferralOriginBackend = z.object({
  ...ZodTransactionReferralOrigin.omit({
    user_id: true,
  }).shape,
  user_id: z.number(),
});
export type TransactionReferralOriginBackend = z.infer<typeof ZodTransactionReferralOriginBackend>;

export const ZodTransactionOrderCancellationReason = z.enum([
  'error',
  'compliance_verification_failed',
  'failed_deposit',
  'insufficient_balance',
  'expired',
  'skipped',
  'user_action',
  'order_not_tradable',
  'shares_agent',
  'sof_task_rejected',
]);

export type TransactionOrderCancellationReason = z.infer<
  typeof ZodTransactionOrderCancellationReason
>;

export const ZodTransactionOrderCancellationInitiator = z.enum(['user', 'bo', 'system']);

export type TransactionOrderCancellationInitiator = z.infer<
  typeof ZodTransactionOrderCancellationInitiator
>;

export const ZodTriggeredOrderCancellationReason = z.enum([
  'unable_to_process_payment',
  'general_error',
  'asset_not_tradable',
  'no_card_authorized',
  'payment_has_timeout',
  'order_expired',
]);

export type TriggeredOrderCancellationReason = z.infer<typeof ZodTriggeredOrderCancellationReason>;

export const ZodTransactionActionStatus = z.enum(['pending', 'completed', 'cancelled', 'deleted']);

export type TransactionActionStatus = z.infer<typeof ZodTransactionActionStatus>;

// ProfileActivityTransaction that includes all possible status values
export const ZodProfileActivityTransactionStatus = z.enum([
  'pending',
  'completed',
  'cancelled',
  'deleted',
  'filled',
  'expired',
  'rejected',
  'skipped',
  'done',
  'executed',
  'pending_funds',
  'pending_approval',
  'active',
  'shares_canceled',
  'shares_pending_campaign',
  'initiated',
]);

export type ProfileActivityTransactionStatus = z.infer<typeof ZodProfileActivityTransactionStatus>;

export const ZodTransactionActionPeriod = z.enum(['day', 'week', 'month', 'year']);

export type TransactionActionPeriod = z.infer<typeof ZodTransactionActionPeriod>;

export const ZodLinkingType = z.enum(['manual_transfer', 'internal_transfer']);

export const ZodProfileActivityTransactionType = z.enum([
  'top_up',
  'withdraw',
  'referral_gift',
  'referral_equity_gift',
  'commercial_gesture',
  'card_top_up',
  'card_authorization',
  'dividend',
  'merger',
  'spinoff',
  'apple_pay_top_up',
  'buy',
  'sell',
  'card_withdraw',
  'investment_plan_buy',
  'investment_plan_sell',
  'auto_invest',
  'employee_savings_custody_fee',
  'employee_savings_management_fee',
  'employee_savings_outgoing_transfer',
  'employee_savings_payout',
  'employee_savings_withdrawal',
  'employee_savings_early_withdrawal',
  'employee_savings_campaign_contribution',
  'fully_managed_plan_contribution',
  'fully_managed_plan_matching_contribution',
  'fully_managed_portfolio_rebalancing',
  'triggered_order_buy',
  'triggered_order_sell',
  'apple_pay_authorization',
  'managed_investment_plan_management_fee',
  'wm_recurring_fee',
  'voluntary_payment',
  'arbitrage',
  'matching_contribution',
  'b2b_incoming_external_transfer', // used in ProfileTransactionDocumentTypeValues
  'b2b_matching_contribution_order',
  'b2b_triggered_arbitrage_order',
  'b2b_planned_withdrawal_order',
  'taiga_generic_transaction',
  'crypto_transfer',
  'capital_increase',
]);

export type ProfileActivityTransactionType = z.infer<typeof ZodProfileActivityTransactionType>;

export const ZodTransactionTriggeredOrderStatus = z.enum(['completed', 'cancelled']);
export type TransactionTriggeredOrderStatus = z.infer<typeof ZodTransactionTriggeredOrderStatus>;
